import {FC, useEffect, useMemo, useState} from 'react'
import {api, AuthUser, ApiError} from '../../services/api'
import {Pagination} from '../../components/Pagination'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {PageHead} from '../../components/PageHead'
import {ListToolbar, FilterSelect} from '../../components/ListToolbar'
import {ModalPortal} from '../../components/ModalPortal'
import {useIsMobile} from '../../hooks/useIsMobile'

const PAGE_SIZE = 10

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'})

const StatusPill: FC<{status: AuthUser['accountStatus']}> = ({status}) => {
  const active = status === 'ACTIVE'
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        borderRadius: 999,
        padding: '4px 10px',
        color: active ? '#1f9d5b' : '#d9214e',
        background: active ? 'rgba(3,149,64,0.12)' : 'rgba(217,33,78,0.1)',
      }}
    >
      {active ? 'Active' : 'Disabled'}
    </span>
  )
}

/** Module 2 · admin Accounts management — list, search, enable/disable, edit. */
const AccountsPage: FC = () => {
  const [users, setUsers] = useState<AuthUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [editing, setEditing] = useState<AuthUser | null>(null)
  const isMobile = useIsMobile()

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setUsers(await api.listUsers('RENTER'))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load accounts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return users.filter((u) => {
      const matchesSearch =
        !q || [u.firstName, u.lastName, u.email].filter(Boolean).join(' ').toLowerCase().includes(q)
      const matchesRole = !role || u.role === role
      const matchesStatus =
        !status || (status === 'ACTIVE' ? u.accountStatus === 'ACTIVE' : u.accountStatus !== 'ACTIVE')
      return matchesSearch && matchesRole && matchesStatus
    })
  }, [users, search, role, status])

  // Reset to page 1 when the search or any filter changes, and clamp if the list shrinks.
  useEffect(() => setPage(1), [search, role, status])
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const toast = useToast()
  const toggleStatus = async (u: AuthUser) => {
    setBusyId(u.id)
    try {
      const next = u.accountStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'
      const updated = await api.setUserStatus(u.id, next)
      setUsers((list) => list.map((x) => (x.id === u.id ? updated : x)))
      toast.success(`${u.firstName} ${u.lastName} ${next === 'DISABLED' ? 'disabled' : 'enabled'}.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update status')
    } finally {
      setBusyId(null)
    }
  }

  const onSaved = (updated: AuthUser) => {
    setUsers((list) => list.map((x) => (x.id === updated.id ? updated : x)))
    toast.success('Account updated.')
  }

  return (
    <div>
      <PageHead
        icon='👥'
        eyebrow='People'
        title='Accounts'
        sub='Manage renter accounts — enable, disable, and edit.'
      />

      <ListToolbar search={search} onSearch={setSearch} placeholder='Search by name or email…'>
        <FilterSelect
          value={role}
          onChange={setRole}
          ariaLabel='Filter by role'
          options={[
            {label: 'All roles', value: ''},
            {label: 'Admin', value: 'ADMIN'},
            {label: 'Renter', value: 'RENTER'},
          ]}
        />
        <FilterSelect
          value={status}
          onChange={setStatus}
          ariaLabel='Filter by status'
          options={[
            {label: 'All statuses', value: ''},
            {label: 'Active', value: 'ACTIVE'},
            {label: 'Disabled', value: 'DISABLED'},
          ]}
        />
      </ListToolbar>

      <div className='card ccp-fade-up-2' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
        <div className='card-body p-0'>
          {loading ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading accounts…</div>
          ) : error ? (
            <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>
              {error}
              <div style={{marginTop: 12}}>
                <button onClick={load} style={btnGhost}>Retry</button>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>
              {users.length === 0 ? 'No renter accounts yet.' : 'No accounts match your search.'}
            </div>
          ) : isMobile ? (
            <div>
              {paged.map((u) => (
                <div key={u.id} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                    <div style={{minWidth: 0}}>
                      <div style={{fontWeight: 600, color: INK}}>{u.firstName} {u.lastName}</div>
                      <div style={{color: MUTED, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{u.email}</div>
                    </div>
                    <StatusPill status={u.accountStatus} />
                  </div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, fontSize: 13, color: MUTED}}>
                    <span>{u.phone || '—'}</span>
                    <span>Joined {fmtDate(u.createdAt)}</span>
                  </div>
                  <div style={{display: 'flex', gap: 8, marginTop: 12}}>
                    <button onClick={() => setEditing(u)} style={{...btnGhost, flex: 1}}>Edit</button>
                    <button
                      onClick={() => toggleStatus(u)}
                      disabled={busyId === u.id}
                      style={{...(u.accountStatus === 'ACTIVE' ? btnDanger : btnPrimary), flex: 1}}
                    >
                      {busyId === u.id ? <Spinner size={13} /> : u.accountStatus === 'ACTIVE' ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                <thead>
                  <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Phone</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Joined</th>
                    <th style={{...thStyle, textAlign: 'right'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((u) => (
                    <tr key={u.id} style={{borderTop: '1px solid #f1f3f7'}}>
                      <td style={tdStyle}>
                        <span style={{fontWeight: 600, color: INK}}>{u.firstName} {u.lastName}</span>
                      </td>
                      <td style={{...tdStyle, color: MUTED}}>{u.email}</td>
                      <td style={{...tdStyle, color: MUTED}}>{u.phone || '—'}</td>
                      <td style={tdStyle}><StatusPill status={u.accountStatus} /></td>
                      <td style={{...tdStyle, color: MUTED}}>{fmtDate(u.createdAt)}</td>
                      <td style={{...tdStyle, textAlign: 'right', whiteSpace: 'nowrap'}}>
                        <button onClick={() => setEditing(u)} style={btnGhost}>Edit</button>
                        <button
                          onClick={() => toggleStatus(u)}
                          disabled={busyId === u.id}
                          style={{...(u.accountStatus === 'ACTIVE' ? btnDanger : btnPrimary), minWidth: 72}}
                        >
                          {busyId === u.id ? <Spinner size={13} /> : u.accountStatus === 'ACTIVE' ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div style={{borderTop: '1px solid #f1f3f7'}}>
              <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
            </div>
          )}
        </div>
      </div>

      {editing && <ModalPortal><EditModal user={editing} onClose={() => setEditing(null)} onSaved={onSaved} /></ModalPortal>}
    </div>
  )
}

const EditModal: FC<{user: AuthUser; onClose: () => void; onSaved: (u: AuthUser) => void}> = ({user, onClose, onSaved}) => {
  const [firstName, setFirstName] = useState(user.firstName)
  const [lastName, setLastName] = useState(user.lastName)
  const [phone, setPhone] = useState(user.phone ?? '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setSaving(true)
    setErr(null)
    try {
      const updated = await api.updateUser(user.id, {firstName, lastName, phone: phone.trim() || null})
      onSaved(updated)
      onClose()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save')
      setSaving(false)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '28px 26px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Edit account</h2>
        <p style={{color: MUTED, fontSize: 13, margin: '0 0 20px'}}>{user.email}</p>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 16}}>{err}</div>}
        <div style={{display: 'flex', gap: 12, marginBottom: 14}}>
          <Field label='First name' value={firstName} onChange={setFirstName} />
          <Field label='Last name' value={lastName} onChange={setLastName} />
        </div>
        <div style={{marginBottom: 22}}>
          <Field label='Phone' value={phone} onChange={setPhone} placeholder='Optional' />
        </div>
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10}}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={saving || !firstName.trim() || !lastName.trim()} style={{...btnPrimary, minWidth: 120}}>
            {saving ? <Spinner size={14} /> : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

const Field: FC<{label: string; value: string; onChange: (v: string) => void; placeholder?: string}> = ({label, value, onChange, placeholder}) => (
  <div style={{flex: 1}}>
    <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{label}</label>
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none'}}
    />
  </div>
)

const thStyle: React.CSSProperties = {padding: '14px 18px', fontWeight: 600}
const tdStyle: React.CSSProperties = {padding: '14px 18px', verticalAlign: 'middle'}
const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '7px 14px', cursor: 'pointer', marginLeft: 8, border: '1px solid transparent'}
const btnGhost: React.CSSProperties = {...btnBase, marginLeft: 0, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}

export {AccountsPage}
